import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router";
import axios from "axios";

import { AppContext } from "../../context.js";

import { FilePlus, ChevronDown, ChevronUp, ChevronDown2, ChevronUp2, Minus2, MoreVertical, Lock } from "../../files/icons/index.js";
import { Status, Button } from "./component.js";
import TaskCard from "./taskCard.js";
import Editor from "./editor.js";
import Drop from "./drop.js";
import PhysicianPicker from "./physicianPicker.js";

// ProjectTasks renders a project's task list inline (as opposed to the ProjectCard
// summary that links out). It loads the tasks for the given project and delegates each
// to TaskCard, mirroring the task section of the full project page.
function ProjectTasks({ projectID, project, patient, settings, users, onProjectChange, onPatientChange, hasCognitiveStaging, onCognitiveStagingChange, readOnly = false }) {
  const context = useContext(AppContext);
  const navigate = useNavigate();

  const [tasks, setTasks] = useState([]);

  const template = (settings.projectTemplates || []).find((t) => t.id === project.templateID);

  // Create task state.
  const [closeCreateTaskDrop, setCloseCreateTaskDrop] = useState(0);
  const [createTask, setCreateTask] = useState({ visible: false, template: null, userID: null, title: null, description: null });

  // Task filter state (client-side, applied over the already-loaded task list).
  // Tasks are always loaded and rendered in manual "order" sequence.
  const [tasksFilter, setTasksFilter] = useState({ statusType: ["TO_DO", "IN_PROGRESS", "PAUSED", "COMPLETED", "CANCELED"], userID: [] });
  const [closeTaskStatusFilterDrop, setCloseTaskStatusFilterDrop] = useState(0);
  const [closeTaskUserFilterDrop, setCloseTaskUserFilterDrop] = useState(0);

  // Task manual reorder (drag and drop) state.
  const [dragTaskID, setDragTaskID] = useState(null);
  const [dragOverTaskID, setDragOverTaskID] = useState(null);

  // Project priority state.
  const [closeProjectPriorityDrop, setCloseProjectPriorityDrop] = useState(0);

  // Project "more" menu state (archive/unarchive, export).
  const [closeMoreDrop, setCloseMoreDrop] = useState(0);

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectID]);

  function loadTasks() {
    let u = context.addSpiner();
    axios.post("/api/admin/tasks", {
      filter: {
        projectID: [projectID],
        statusType: ["TO_DO", "IN_PROGRESS", "PAUSED", "COMPLETED", "CANCELED"],
        userID: [],
      },
      sorter: { field: "order", order: "asc" },
    }).then((response) => {
      setTasks(response.data.tasks);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Drag-and-drop task reorder — optimistically splices the full tasks array,
  // persists the new order, and rolls back on error. Mirrors reorderTaskActions
  // in taskCard.js, but operates on the parent's task list since tasks (unlike
  // actions) are top-level documents owned by this component, not TaskCard.
  function reorderTasks(fromIndex, toIndex) {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    const previousTasks = tasks || [];
    const reordered = [...previousTasks];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setTasks(reordered);
    let u = context.addSpiner();
    axios.post('/api/admin/tasks/reorder', {
      projectID: projectID,
      taskIDs: reordered.map((t) => t.id),
    }).catch((e) => {
      context.addError(e);
      setTasks(previousTasks);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // After a task change may have auto-completed the project, refresh the task list and
  // notify the parent so lifecycle-derived UI (step completion / lock state) stays in sync.
  function handleAutoComplete() {
    loadTasks();
    if (onProjectChange) onProjectChange();
  }

  function statusColor() {
    if (!template) return "grey";
    const s = (template.statuses || []).find((ps) => ps.id === project.statusID);
    return s ? s.color : "grey";
  }

  // Create task
  function createTaskOnClick(taskTemplate) {
    setCreateTask({ visible: true, template: taskTemplate, userID: null, title: null, description: null });
    setCloseCreateTaskDrop((prev) => prev + 1);
  }

  function createTaskUserUpdate(user) {
    setCreateTask((prev) => ({ ...prev, userID: user.id }));
  }

  function createTaskTitleUpdate(text) {
    setCreateTask((prev) => ({ ...prev, title: text }));
  }

  function createTaskDescriptionUpdate(text) {
    setCreateTask((prev) => ({ ...prev, description: text }));
  }

  function scrollToTask(id) {
    if (!id) return;
    setTimeout(() => {
      const element = document.getElementById("task_" + id);
      if (element) element.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
  }

  function createTaskConfirm() {
    let u = context.addSpiner();
    axios.post("/api/admin/tasks/create", {
      projectID: projectID,
      templateID: createTask.template.id,
      userID: createTask.userID,
      title: createTask.title,
      description: createTask.description,
    }).then((response) => {
      setCreateTask({ visible: false });
      setTasks((prev) => [...prev, response.data]);
      setCloseCreateTaskDrop((prev) => prev + 1);
      scrollToTask(response.data.id);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Task filter and sorter handlers (client-side over the loaded task list).
  function toggleTaskStatusFilter(status) {
    setTasksFilter((prev) => {
      const currentStatuses = prev.statusType || [];
      if (currentStatuses.includes(status)) {
        if (currentStatuses.length === 1) return prev;
        return { ...prev, statusType: currentStatuses.filter((s) => s !== status) };
      }
      return { ...prev, statusType: [...currentStatuses, status] };
    });
  }

  function setTaskUserFilterAll() {
    setTasksFilter((prev) => ({ ...prev, userID: [] }));
    setCloseTaskUserFilterDrop((prev) => prev + 1);
  }

  function setTaskUserFilterMe() {
    setTasksFilter((prev) => ({ ...prev, userID: [context.id] }));
    setCloseTaskUserFilterDrop((prev) => prev + 1);
  }

  // Project priority
  function renderProjectPriorityIcon() {
    switch (project.priority) {
      case "NONE":
        return <ChevronDown2 height={16} width={16} color={'blue'} />;
      case "LOW":
        return <ChevronDown height={16} width={16} color={'blue'} />;
      case "MEDIUM":
        return <Minus2 height={16} width={16} color={'orange'} />;
      case "HIGH":
        return <ChevronUp height={16} width={16} color={'red'} />;
      case "CRITICAL":
        return <ChevronUp2 height={16} width={16} color={'red'} />;
      default:
        return <Minus2 height={16} width={16} color={'orange'} />;
    }
  }

  function renderProjectPriorityTitle() {
    if (!template || !template.priorityLabels) return null;
    switch (project.priority) {
      case "NONE":
        return template.priorityLabels[4];
      case "LOW":
        return template.priorityLabels[3];
      case "MEDIUM":
        return template.priorityLabels[2];
      case "HIGH":
        return template.priorityLabels[1];
      case "CRITICAL":
        return template.priorityLabels[0];
      default:
        return template.priorityLabels[2];
    }
  }

  function updateProjectPriority(priority) {
    let u = context.addSpiner();
    axios.post('/api/admin/projects/updatePriority', {
      id: project.id,
      priority: priority,
    }).then(() => {
      setCloseProjectPriorityDrop((prev) => prev + 1);
      if (onProjectChange) onProjectChange();
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  // Archive / unarchive project
  function archiveProject() {
    let u = context.addSpiner();
    axios.post('/api/admin/projects/archive', {
      id: project.id,
    }).then(() => {
      setCloseMoreDrop((prev) => prev + 1);
      if (onProjectChange) onProjectChange();
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function unarchiveProject() {
    let u = context.addSpiner();
    axios.post('/api/admin/projects/unarchive', {
      id: project.id,
    }).then(() => {
      setCloseMoreDrop((prev) => prev + 1);
      if (onProjectChange) onProjectChange();
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  return (
    <>
      {/* Project header: status, priority, more menu, open full project */}
      <div className="d-flex justify-content-between align-items-center mb-2 px-2">
        <div className="d-flex justify-content-start align-items-center gap-2">
          {project.templateName && project.templateName.toLowerCase().startsWith('post-alignment') && (
            <Status color={project.templateColor} muted={true} text={project.templateName} />
          )}
          {project.statusName?.toLowerCase() !== 'to do' && (
            <Status color={statusColor()} muted={true} text={project.statusName} subtext={project.statusReasonName} />
          )}
          {template && template.priorityLabels && (
            readOnly ? (
              <div className={"item grey " + ((project.priority === "NONE" || project.priority === "LOW") ? "blue" : "") + ((project.priority === "HIGH" || project.priority === "CRITICAL") ? "red" : "")} style={{ opacity: 0.7, cursor: "default" }}>
                {renderProjectPriorityIcon()}
                <span className="typo-normal-150">{renderProjectPriorityTitle()}</span>
              </div>
            ) : (
            <Drop close={closeProjectPriorityDrop}>
              <div className={"item grey " + ((project.priority === "NONE" || project.priority === "LOW") ? "blue" : "") + ((project.priority === "HIGH" || project.priority === "CRITICAL") ? "red" : "")}>
                {renderProjectPriorityIcon()}
                <span className="typo-normal-150">{renderProjectPriorityTitle()}</span>
                <ChevronDown height={16} width={16} color={'grey'} />
              </div>
              <div className="card shadowmore" style={{ minWidth: 460, width: 'fit-content' }}>
                <div className="card-header sm">
                  <div className={"item me-1 " + (project.priority === "CRITICAL" ? "grey" : "")} onClick={() => updateProjectPriority("CRITICAL")}>
                    <ChevronUp2 height={16} width={16} color={'red'} /> <span className="typo-normal-150">{template.priorityLabels[0]}</span>
                  </div>
                  <div className={"item me-1 " + (project.priority === "HIGH" ? "grey" : "")} onClick={() => updateProjectPriority("HIGH")}>
                    <ChevronUp height={16} width={16} color={'red'} /> <span className="typo-normal-150">{template.priorityLabels[1]}</span>
                  </div>
                  <div className={"item me-1 " + (project.priority === "MEDIUM" ? "grey" : "")} onClick={() => updateProjectPriority("MEDIUM")}>
                    <Minus2 height={16} width={16} color={'orange'} /> <span className="typo-normal-150">{template.priorityLabels[2]}</span>
                  </div>
                  <div className={"item me-1 " + (project.priority === "LOW" ? "grey" : "")} onClick={() => updateProjectPriority("LOW")}>
                    <ChevronDown height={16} width={16} color={'blue'} /> <span className="typo-normal-150">{template.priorityLabels[3]}</span>
                  </div>
                  <div className={"item " + (project.priority === "NONE" ? "grey" : "")} onClick={() => updateProjectPriority("NONE")}>
                    <ChevronDown2 height={16} width={16} color={'blue'} /> <span className="typo-normal-150">{template.priorityLabels[4]}</span>
                  </div>
                </div>
              </div>
            </Drop>
            )
          )}
        </div>
        <div className="d-flex justify-content-end align-items-center gap-2">
          {template && template.taskTemplates && template.taskTemplates.length > 0 && (
            !(readOnly || patient?.statusType === "ARCHIVED") ? (
            <Drop close={closeCreateTaskDrop} side={"right"}>
              <div className="item typo-normal-150 ms-2"><FilePlus height={16} width={16} color={'grey'} /> Create Task</div>
              <div className="card shadowmore" style={{ width: 'fit-content' }}>
                <div className="card-header sm">
                  {template.taskTemplates.slice().sort((a, b) => a.title.localeCompare(b.title)).map((tt) => (
                    <div key={tt.id} className="item" style={{ minWidth: 260 }} onClick={() => createTaskOnClick(tt)}>
                      <div className="w-100">
                        <div className="typo-header-150">{tt.title}</div>
                        <div className="typo-normal-125">{tt.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Drop>
            ) : (
              <div className="item typo-normal-150 ms-2 disabled" style={{opacity: 0.5, cursor: 'not-allowed'}}><Lock height={14} width={14} color={'grey'} /> Create Task</div>
            )
          )}
        </div>
      </div>

      {/* Task filter and sort bar */}
      <div className="d-flex justify-content-between align-items-center mb-2 px-2">
        <div className="d-flex justify-content-start align-items-center">
          <div className="typo-normal-125 me-1">Filter by:</div>
          <Drop close={closeTaskStatusFilterDrop}>
            <div className="item gap-no typo-normal-125 me-1">
              Status: {tasksFilter.statusType.length === 5 ? "All" : tasksFilter.statusType.map(s => s.replace("_", " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase())).join(", ")} <ChevronDown height={12} width={12} color={'grey'} />
            </div>
            <div className="card" style={{ width: 200 }}>
              <div className="card-header sm">
                <div className={"side-item small typo-medium-150 mb-1 " + (tasksFilter.statusType.includes("TO_DO") ? "active" : "")} onClick={() => toggleTaskStatusFilter("TO_DO")}>
                  <div className="typo-normal-150">To Do</div>
                </div>
                <div className={"side-item small typo-medium-150 mb-1 " + (tasksFilter.statusType.includes("IN_PROGRESS") ? "active" : "")} onClick={() => toggleTaskStatusFilter("IN_PROGRESS")}>
                  <div className="typo-normal-150">In Progress</div>
                </div>
                <div className={"side-item small typo-medium-150 mb-1 " + (tasksFilter.statusType.includes("PAUSED") ? "active" : "")} onClick={() => toggleTaskStatusFilter("PAUSED")}>
                  <div className="typo-normal-150">Paused</div>
                </div>
                <div className={"side-item small typo-medium-150 mb-1 " + (tasksFilter.statusType.includes("COMPLETED") ? "active" : "")} onClick={() => toggleTaskStatusFilter("COMPLETED")}>
                  <div className="typo-normal-150">Completed</div>
                </div>
                <div className={"side-item small typo-medium-150 mb-1 " + (tasksFilter.statusType.includes("CANCELED") ? "active" : "")} onClick={() => toggleTaskStatusFilter("CANCELED")}>
                  <div className="typo-normal-150">Canceled</div>
                </div>
              </div>
            </div>
          </Drop>
          <Drop close={closeTaskUserFilterDrop} className="me-1">
            <div className="item gap-no typo-normal-125 me-1">
              Assignee: {tasksFilter.userID.length === 0 ? "All" : (tasksFilter.userID[0] === context.id ? "Only me" : "All")} <ChevronDown height={12} width={12} color={'grey'} />
            </div>
            <div className="card" style={{ width: 200 }}>
              <div className="card-header sm">
                <div className={"side-item small typo-medium-150 mb-1 " + (tasksFilter.userID.length === 0 ? "active" : "")} onClick={setTaskUserFilterAll}>
                  <div className="typo-normal-150">All</div>
                </div>
                <div className={"side-item small typo-medium-150 mb-1 " + (tasksFilter.userID.length > 0 && tasksFilter.userID[0] === context.id ? "active" : "")} onClick={setTaskUserFilterMe}>
                  <div className="typo-normal-150">Only me</div>
                </div>
              </div>
            </div>
          </Drop>
        </div>
      </div>

      {createTask.visible && (
        <div className="fixed-bottom modal-bg" onClick={() => setCreateTask({ visible: false })}>
          <div className="container" style={{ paddingTop: '5%' }}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => { e.stopPropagation(); }}>
                  <div className="card-header sm">
                    <span className="typo-strong-300">Create task</span><br/>
                    <span className="typo-normal-175">Fill fields below and click create button</span>
                  </div>
                  <div className="card-content sm">
                    <input type="text" className="input borderless typo-strong-200 w-100 mb-2" placeholder="Title..." value={createTask.title || ""} onChange={(e) => createTaskTitleUpdate(e.target.value)} autoFocus={true} />
                    <Editor
                      placeholder="Task description..."
                      value={createTask.description}
                      onBlur={createTaskDescriptionUpdate}
                      toolbar="limited"
                      className="text-editor borderless"
                    />
                    <div className="d-flex justify-content-start align-items-center mt-2">
                      <div className="w-50">
                        <div className="typo-normal-150">Assignee:</div>
                        <PhysicianPicker
                          physicianList={users}
                          physicianID={createTask.userID}
                          onChange={createTaskUserUpdate}
                          onChangeID={null}
                          rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="card-content sm text-end">
                    <Button onClick={createTaskConfirm} color="blue" text="Create" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {(() => {
        // Only render tasks whose task template belongs to this project's template.
        // TaskCard reads projectTemplate.taskTemplates[...].statuses unguarded, so a task
        // paired with the wrong template would crash.
        let renderable = (tasks || []).filter((task) =>
          template && (template.taskTemplates || []).some((tt) => tt.id === task.templateID)
        );

        // Apply status/assignee filters. Tasks are already in manual "order" sequence
        // from the server, so no client-side sort is applied here.
        renderable = renderable.filter((task) => tasksFilter.statusType.includes(task.statusType));
        if (tasksFilter.userID.length > 0) {
          renderable = renderable.filter((task) => tasksFilter.userID.includes(task.userID));
        }

        if (renderable.length === 0) {
          return (
            <div className="d-flex justify-content-center align-items-center mt-4">
              <div className="typo-normal-150 text-muted">No tasks</div>
            </div>
          );
        }

        const canReorder = tasks.length > 1 && !readOnly;

        return renderable.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            users={users}
            settings={settings}
            projectTemplate={template}
            projectID={projectID}
            onProjectAutoComplete={handleAutoComplete}
            onPatientChange={onPatientChange}
            patient={patient}
            hasCognitiveStaging={hasCognitiveStaging}
            onCognitiveStagingChange={onCognitiveStagingChange}
            onTaskDuplicated={(duplicatedTask) => setTasks((prev) => [...prev, duplicatedTask])}
            onTaskDeleted={(deletedID) => setTasks((prev) => prev.filter((t) => t.id !== deletedID))}
            canReorder={canReorder}
            isReorderDragging={dragTaskID === task.id}
            isReorderDragOver={dragOverTaskID === task.id}
            onReorderDragStart={(id) => setDragTaskID(id)}
            onReorderDragOver={(id) => {
              if (!dragTaskID) return;
              if (dragOverTaskID !== id) setDragOverTaskID(id);
            }}
            onReorderDrop={(id) => {
              if (!dragTaskID) return;
              const fromIndex = tasks.findIndex((t) => t.id === dragTaskID);
              const toIndex = tasks.findIndex((t) => t.id === id);
              setDragTaskID(null);
              setDragOverTaskID(null);
              reorderTasks(fromIndex, toIndex);
            }}
            onReorderDragEnd={() => {
              setDragTaskID(null);
              setDragOverTaskID(null);
            }}
            readOnly={readOnly}
          />
        ));
      })()}
    </>
  );
}

export default ProjectTasks;
